import { BehaviorSubject } from 'rxjs';
import { addError } from './Error';
import {
  getProjects as getProjectsApi,
  getProjectById as getProjectByIdApi,
  RelatedInventoryRiskItem,
  RiskCounts
} from '../Api/Projects/projectsApi';
import { Account, AddressInfo, CustomFieldValue, Product } from '../types';
import { getSelectedAccountIdsForUrl } from './User';
import { Tag } from './Tags';

export interface Project {
  id: string;
  name: string;
  progress: string;
  siteName: string;
  supplier: Account;
  product: Product;
  status: string;
  nextSteps: string;
  projectStartDate: string;
  serviceCount?: number | null;
  account: Account;
  tags: Tag[];
  managedInClientPortal?: boolean;
  managedInClientPortalBy?: string;
  managedInClientPortalDateTime?: string;
  addressInfo?: AddressInfo;
  // Risk-widget data from the project show endpoint (gated on the projects_updates flag).
  riskCounts?: RiskCounts;
  relatedInventory?: RelatedInventoryRiskItem[];
  singleAsset?: boolean;
  // Main Table (entity_type "Project") values from the index endpoint.
  custom_fields?: CustomFieldValue[];
  // Details view (entity_type "ProjectDetail") values from the show endpoint; kept separate so a
  // show refetch (e.g. adding a tag) can't overwrite the Main Table's custom_fields.
  detailCustomFields?: CustomFieldValue[];
}

const defaultProjectData: Project[] = [];
const projectSubject = new BehaviorSubject<Project[]>(defaultProjectData);
const projectsLoadingSubject = new BehaviorSubject<boolean>(false);

export const getProjectSubject = (): BehaviorSubject<Project[]> => projectSubject;
export const getProjectLoadingSubject = (): BehaviorSubject<boolean> => projectsLoadingSubject;
const emitErrorState = (message?: string): void => addError('project', message);

export const getProjects = (): Project[] => projectSubject.value;
export const getProjectById = (id: string): Project | undefined =>
  projectSubject.value.find((project) => project.id === id);

// Patch just the custom_fields of a project in the store so grids/cards that re-read it on remount
// reflect an inline edit without waiting for a full refetch (entity_type "Project").
export const setProjectCustomFields = (projectId: string, customFields: CustomFieldValue[]): void =>
  projectSubject.next(
    projectSubject.value.map((p) => (p.id === projectId ? { ...p, custom_fields: customFields } : p))
  );

// Same, for one of a project's related-inventory rows (entity_type "ProjectDetail").
export const setRelatedInventoryCustomFields = (
  projectId: string,
  assetId: string,
  customFields: CustomFieldValue[]
): void =>
  projectSubject.next(
    projectSubject.value.map((p) =>
      p.id === projectId
        ? {
            ...p,
            relatedInventory: p.relatedInventory?.map((it) =>
              it.id === assetId ? { ...it, custom_fields: customFields } : it
            )
          }
        : p
    )
  );

// Per project, the read most recently asked for; an older one answering after it is stale.
const latestReadById = new Map<string, number>();
const readsInFlightById = new Map<string, number>();
let readCount = 0;

// Risk scored before a save is stale: clear it, and outdate any read of the project already running.
export const dropProjectRisk = (projectId: string): void => {
  latestReadById.set(projectId, ++readCount);
  projectSubject.next(projectSubject.value.map((p) => (p.id === projectId ? { ...p, riskCounts: undefined } : p)));
};

export const fetchProjects = async (forceRefresh: boolean = false): Promise<void> => {
  if (projectsLoadingSubject.value || (!forceRefresh && projectSubject.getValue().length > 0)) {
    return;
  }

  projectsLoadingSubject.next(true);
  try {
    const { data, error } = await getProjectsApi(getSelectedAccountIdsForUrl());

    if (data) {
      projectSubject.next(data);
      emitErrorState();
    }

    if (error) {
      emitErrorState(error.message);
      projectSubject.next(defaultProjectData);
    }
  } catch (e) {
    // A throw here would otherwise wedge the loading flag on, and every reader of the store waits on it.
    emitErrorState(e instanceof Error ? e.message : String(e));
    projectSubject.next(defaultProjectData);
  } finally {
    projectsLoadingSubject.next(false);
  }
};

const countReadInFlight = (id: string, change: 1 | -1) =>
  readsInFlightById.set(id, (readsInFlightById.get(id) ?? 0) + change);

// For a save a running read may have started too early to see: one more read, which then wins.
export const rereadProjectIfReading = async (id: string, includeRelatedInventory = false): Promise<void> => {
  if (readsInFlightById.get(id)) await fetchProjectById(id, includeRelatedInventory);
};

export const fetchProjectById = async (id: string, includeRelatedInventory = false): Promise<void> => {
  if (projectsLoadingSubject.value) {
    return;
  }

  const read = ++readCount;
  latestReadById.set(id, read);
  countReadInFlight(id, 1);
  const { data, error } = await getProjectByIdApi(id, includeRelatedInventory).finally(() => countReadInFlight(id, -1));
  if (latestReadById.get(id) !== read) return;

  if (data) {
    const currentProjects = projectSubject.getValue();
    const updatedProjects = currentProjects.map((project) => {
      if (project.id === id) {
        // The show endpoint decorates custom_fields with "ProjectDetail" values; route them to
        // detailCustomFields and keep the existing Main Table ("Project") values intact.
        return {
          ...data,
          custom_fields: project.custom_fields ?? data.custom_fields,
          detailCustomFields: data.custom_fields
        };
      }
      return project;
    });
    projectSubject.next(updatedProjects);
    emitErrorState();
  }

  if (error) {
    emitErrorState(error.message);
  }
};
